import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { statusPhrase } from '../util/statusPhrase.js';
/**
 * Rex Mortis — Good Deeds (the lawful mirror of Misdeeds)
 *
 * Structurally a twin of the Misdeeds page: category accordions, deed cards,
 * an attempt flow, an outcome banner, and a career ledger. The differences are
 * thematic — Renown (not Cunning/Infamy) is the gate + reward, there is no
 * Dungeon for a failed deed, and a TRIUMPH grants modest XP (so deeds can tick
 * your level up — the global level-up modal fires off the store update here).
 */
import { mountShell } from '../components/Shell.js';
import { flashToast } from '../util/toast.js';
import { showLootChanceDialog } from '../components/LootChanceDialog.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatBigNum, formatCrowns } from '../util/formatBig.js';
import {
  GOOD_DEED_CATEGORY_LABELS,
  GOOD_DEED_OUTCOME_LABELS,
  type CategoryMasteryDto,
  type GoodDeedCategory,
  type GoodDeedLedgerDto,
  type GoodDeedListing,
} from '@rex-mortis/shared';
import { masteryChipHtml } from '../util/masteryChip.js';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

const CATEGORY_ORDER: GoodDeedCategory[] = [
  'alms',
  'relief',
  'stewardship',
  'healing',
  'diplomacy',
  'protection',
  'great_undertakings',
];

/** Mirrors balance.ts GOOD_DEED.resolveGrindCap — the most Resolve-max the
 *  good-deed ladder can ever grant. Display-only (equal to the crime cap). */
const RESOLVE_GRIND_CAP = 200;

export async function renderGoodDeeds(root: HTMLElement): Promise<void> {
  let listings: GoodDeedListing[] = [];
  let mastery: Record<string, CategoryMasteryDto> = {};
  let ledger: GoodDeedLedgerDto | null = null;
  // Resolve-cost reduction from the Pious Writs perk (0 if not owned).
  let resolveDiscountPct = 0;
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const idx = await api.listGoodDeeds();
    listings = idx.deeds;
    mastery = idx.mastery ?? {};
    ledger = idx.ledger;
    resolveDiscountPct = idx.resolveDiscountPct ?? 0;
  } catch {
    // tolerated — the page still renders
  }

  let fatigueTimer: number | null = null;
  // Remember which deed varieties the player had open — across rebuilds and
  // across visits (localStorage). `null` = no saved choice yet, so we seed the
  // first populated category on first load; after any toggle, the exact open
  // set is restored on return instead of snapping back to the first.
  // Scoped per account so a shared browser (or a fresh character registered
  // on a machine that played before) doesn't inherit another player's open
  // accordion set — a newcomer must land on the first (gentlest) variety.
  const OPEN_CATS_KEY = `rexmortis.gooddeeds.openCats.${store.get().account?.id ?? 'anon'}`;
  const loadOpenCats = (): Set<string> | null => {
    try {
      const raw = localStorage.getItem(OPEN_CATS_KEY);
      if (raw === null) return null;
      const arr = JSON.parse(raw) as string[];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return null;
    }
  };
  const saveOpenCats = (set: Set<string>): void => {
    try {
      localStorage.setItem(OPEN_CATS_KEY, JSON.stringify([...set]));
    } catch {
      /* storage unavailable — non-fatal */
    }
  };
  let openCats: Set<string> | null = loadOpenCats();

  mountShell(root, (main) => {
    // ── Live-update helpers ───────────────────────────────────────────────────
    // An attempt updates the page IN PLACE (resource readout, ledger, the acted
    // card, sibling affordability) instead of rebuilding the whole page — a full
    // rebuild collapsed accordions, re-mounted the chain panel async, and made
    // the viewport jump. Attempts no longer call renderMain.
    const resourceHtml = (char: NonNullable<ReturnType<typeof store.get>['character']>): string => `
      <div class="panel">
        <dl class="kv">
          <dt>Resolve</dt><dd>${char.resolve} / ${char.resolveMax}</dd>
          <dt>Bonus Resolve from Good Deeds</dt><dd>+${char.goodDeedResolveBonus} / +${RESOLVE_GRIND_CAP}</dd>
          <dt>Virtue</dt><dd>${formatBigNum(char.virtue)}</dd>
          <dt>Honour</dt><dd>${formatBigNum(char.honour)}</dd>
          <dt>Level</dt><dd>${char.level}</dd>
          <dt>Status</dt><dd>${char.status}${char.statusUntil ? ' until ' + new Date(char.statusUntil).toLocaleTimeString() : ''}</dd>
        </dl>
        ${
          char.status !== 'OKAY'
            ? `<p class="alert danger" style="margin-top:12px">You cannot perform a good deed while ${statusPhrase(char.status)}.</p>`
            : ''
        }
      </div>`;

    /** Build one fully-wired deed card as a fresh node. */
    const buildCard = (deed: GoodDeedListing): HTMLElement => {
      const char = store.get().character!;
      const card = document.createElement('div');
      card.className = 'deed-card';
      card.dataset.cardKey = deed.key;
      card.innerHTML = deedCardInnerHtml(deed, char, resolveDiscountPct);
      wireCard(card, deed);
      return card;
    };

    /** Re-render the resource readout, ledger, and every visible card in place. */
    const applyLiveUpdate = (): void => {
      const char = store.get().character;
      if (!char) return;
      const rHost = main.querySelector<HTMLElement>('#resource-host');
      if (rHost) rHost.innerHTML = resourceHtml(char);
      const lHost = main.querySelector<HTMLElement>('#ledger-host');
      if (lHost) lHost.innerHTML = deedLedgerHtml(ledger);
      main.querySelectorAll<HTMLElement>('[data-card-key]').forEach((el) => {
        const deed = listings.find((d) => d.key === el.dataset.cardKey);
        if (deed) el.replaceWith(buildCard(deed));
      });
      setupFatigueCountdowns();
    };

    /** Wire one card's gift popup + attempt button. */
    function wireCard(card: HTMLElement, deed: GoodDeedListing): void {
      const giftRow = card.querySelector<HTMLElement>('[data-loot]');
      if (giftRow) {
        const open = (ev: Event): void => {
          ev.preventDefault();
          ev.stopPropagation();
          showLootChanceDialog({
            title: deed.name,
            dropChance: deed.dropChance,
            dropTable: deed.dropTable,
            dropLabel: 'Gifts',
          });
        };
        giftRow.addEventListener('click', open);
        giftRow.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') open(e);
        });
      }

      const btn = card.querySelector<HTMLButtonElement>('button[data-attempt]');
      btn?.addEventListener('click', async () => {
        const key = btn.dataset.attempt!;
        const stakeNum = Number(btn.dataset.stake ?? '0');
        if (stakeNum > 0) {
          const { showConfirmDialog } = await import('../components/ConfirmDialog.js');
          const ok = await showConfirmDialog({
            title: btn.dataset.name ?? 'Pledge the endowment?',
            message: `${formatCrowns(stakeNum)} crowns are pledged before the work begins. A Triumph returns them with the reward, a Partial returns them alone — an unfulfilled undertaking forfeits the endowment.`,
            confirmLabel: 'Pledge it',
            tone: 'gold',
          });
          if (!ok) return;
        }
        main.querySelectorAll<HTMLButtonElement>('button[data-attempt]').forEach((b) => (b.disabled = true));
        try {
          const result = await api.attemptGoodDeed({ deedKey: key });
          store.set({ character: result.character });
          flashToast(buildFlash(result));
          try {
            const idx = await api.listGoodDeeds();
            listings = idx.deeds;
            mastery = idx.mastery ?? {};
            ledger = idx.ledger;
            resolveDiscountPct = idx.resolveDiscountPct ?? 0;
          } catch { /* keep stale listing */ }
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'The deed could not be done.';
          flashToast({ kind: 'danger', message: msg });
        } finally {
          applyLiveUpdate();
        }
      });
    }

    /** (Re)bind the live fatigue countdowns across the current cards. */
    function setupFatigueCountdowns(): void {
      if (fatigueTimer !== null) {
        window.clearInterval(fatigueTimer);
        fatigueTimer = null;
      }
      const cdEls = Array.from(main.querySelectorAll<HTMLElement>('.deed-fatigue-cd'));
      if (cdEls.length === 0) return;
      let refreshing = false;
      const tick = (): void => {
        let anyExpired = false;
        for (const el of cdEls) {
          const txt = fatigueCountdown(el.dataset.reset);
          if (txt) { el.textContent = txt; } else { anyExpired = true; el.textContent = 'now'; }
        }
        if (anyExpired && !refreshing) {
          refreshing = true;
          if (fatigueTimer !== null) { window.clearInterval(fatigueTimer); fatigueTimer = null; }
          void api.listGoodDeeds().then((idx) => {
            listings = idx.deeds;
            mastery = idx.mastery ?? {};
            ledger = idx.ledger;
            resolveDiscountPct = idx.resolveDiscountPct ?? 0;
            applyLiveUpdate();
          }).catch(() => { /* leave stale */ });
        }
      };
      tick();
      fatigueTimer = window.setInterval(tick, 1000);
    }

    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/good-deeds.webp" alt="" onerror="this.remove()" />
        <h1>Good Deeds</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          The first to refuse the crooked road is remembered in every good deed that follows.
        </p>

        <div id="flash"></div>

        <div id="resource-host">${resourceHtml(char)}</div>

        <div id="ledger-host">${deedLedgerHtml(ledger)}</div>

        ${char.virtue < 100
          ? `<details class="deed-section" id="chain-fold">
               <summary>The Lighthouse of the Drowned — a great work for the proven virtuous. Unfold to behold it.</summary>
               <div id="chain-host"></div>
             </details>`
          : `<div id="chain-host"></div>`}

        <div id="cats"></div>
      `;

      // Great Work panel — the multi-stage endgame undertaking (v1.13C).
      // Self-contained: fetches its own state and re-renders the page header
      // after an attempt so Resolve/crowns/status stay in step.
      {
        const chainHost = main.querySelector<HTMLDivElement>('#chain-host')!;
        // NOTE: deliberately NOT named "ChainPanel" — see Misdeeds.ts; the
        // old chunk name tripped ad-blocker crypto filters.
        void import('../components/GreatWorksPanel.js')
          .then(({ mountChainPanel }) => {
            mountChainPanel(chainHost, {
              kind: 'work',
              // Light in-place refresh — the chain panel re-renders its own host,
              // so we must NOT rebuild the whole page.
              onAfterAttempt: () => applyLiveUpdate(),
            });
          })
          .catch(() => {
            // Stale-tab lazy-chunk failure after a deploy — prompt a refresh
            // instead of silently hiding the Great Work.
            chainHost.innerHTML = `
              <div class="panel" style="text-align:center;">
                <p style="margin:0 0 var(--s-2) 0;">A newer version of the realm is live — refresh to see the Great Work.</p>
                <button class="btn" id="chain-refresh">Refresh</button>
              </div>`;
            chainHost.querySelector('#chain-refresh')?.addEventListener('click', () => location.reload());
          });
      }

      const style = document.createElement('style');
      style.textContent = `
        .deed-section {
          margin-bottom: var(--s-3);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          background: var(--c-parchment);
        }
        .deed-section summary {
          cursor: pointer;
          padding: var(--s-2) var(--s-3);
          font-family: var(--font-display);
          color: var(--c-oxblood);
          font-size: 1rem;
          letter-spacing: 0.02em;
          list-style: none;
          user-select: none;
        }
        .deed-section summary::-webkit-details-marker { display: none; }
        .deed-section summary::before { content: '\\25B8\\00A0'; color: var(--c-rule); }
        .deed-section[open] summary::before { content: '\\25BE\\00A0'; }
        .deed-section summary:hover { background: rgba(139, 111, 71, 0.10); }
        .deed-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--s-3);
          padding: 0 var(--s-3) var(--s-3);
        }
        .deed-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex;
          flex-direction: column;
        }
        .deed-card .deed-art {
          display: block;
          width: calc(100% + 2 * var(--s-3));
          margin: calc(-1 * var(--s-3)) calc(-1 * var(--s-3)) var(--s-2);
          height: 170px;
          object-fit: cover;
          border-bottom: 1px solid var(--c-rule);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        /* Great Undertakings art was painted with full-figure subjects — the
           170px strip beheads them. Those cards alone keep the painting's own
           3:2 proportions (capped, slight top bias if a crop kicks in). */
        .deed-card .deed-art--full {
          height: auto;
          aspect-ratio: 3 / 2;
          max-height: 280px;
          object-position: 50% 22%;
        }
        .deed-card h3 { margin: 0 0 4px 0; font-size: 1.05rem; }
        .deed-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.92rem;
          margin-bottom: var(--s-2);
        }
        /* Table hugs the bottom of the blurb text; the button is pushed to the
           card's bottom edge so buttons stay aligned across the row. */
        .deed-card .cg { margin-bottom: auto; }
        /* Cost-vs-reward table (.cg) is now shared — styles live in
           styles/pages.css, markup built via util/costReward.ts. */
        .deed-card button { margin-top: auto; }
        .record-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
          gap: var(--s-2);
          margin-top: var(--s-2);
        }
        .record-block {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-2) var(--s-3);
        }
        .record-block .label {
          font-family: var(--font-display);
          font-size: 0.8rem;
          color: var(--c-ink-soft);
          letter-spacing: 0.02em;
        }
        .record-block .value {
          font-family: var(--font-numeric);
          font-size: 1.2rem;
          font-weight: 700;
        }
        .record-block.win .value { color: var(--c-success); }
      `;
      main.appendChild(style);

      const cats = main.querySelector<HTMLDivElement>('#cats')!;
      let catIndex = 0;
      for (const cat of CATEGORY_ORDER) {
        const inCat = listings.filter((d) => d.category === cat);
        if (inCat.length === 0) continue;

        const section = document.createElement('details');
        section.className = 'deed-section';
        section.dataset.cat = cat;
        if (openCats === null) {
          if (catIndex === 0) section.open = true;
        } else if (openCats.has(cat)) {
          section.open = true;
        }
        catIndex++;

        const summary = document.createElement('summary');
        summary.innerHTML = `${escapeHtml(GOOD_DEED_CATEGORY_LABELS[cat])} (${inCat.length})${masteryChipHtml(mastery[cat])}`;
        section.appendChild(summary);

        const grid = document.createElement('div');
        grid.className = 'deed-grid';

        for (const deed of inCat) {
          grid.appendChild(buildCard(deed));
        }
        section.appendChild(grid);
        // Persist the open set the instant the player toggles a variety, so it
        // sticks even if they navigate away without performing a deed.
        section.addEventListener('toggle', () => {
          const open = new Set<string>();
          cats.querySelectorAll<HTMLDetailsElement>('details.deed-section').forEach((s) => {
            if (s.open && s.dataset.cat) open.add(s.dataset.cat);
          });
          openCats = open;
          saveOpenCats(open);
        });
        cats.appendChild(section);
      }

      // Cards wire their own gift popup + attempt button (see buildCard); an
      // attempt updates the page in place via applyLiveUpdate, never a rebuild.
      setupFatigueCountdowns();
    };

    anchored(renderMain);
  });

  // Stop the countdown ticking once the player navigates away.
  window.addEventListener(
    'hashchange',
    () => {
      if (fatigueTimer !== null) {
        window.clearInterval(fatigueTimer);
        fatigueTimer = null;
      }
    },
    { once: true },
  );
}

function buildFlash(r: import('@rex-mortis/shared').AttemptGoodDeedResponse): FlashState {
  const head = `${GOOD_DEED_OUTCOME_LABELS[r.outcome]} — ${r.deedName}`;
  const tail: string[] = [];

  if (r.outcome === 'TRIUMPH' || r.outcome === 'PARTIAL') {
    if (r.crownsDelta !== '0') tail.push(`+${formatCrowns(r.crownsDelta)} crowns`);
    if (r.virtueDelta > 0) tail.push(`+${r.virtueDelta} virtue`);
    if (r.honourDelta > 0) tail.push(`+${r.honourDelta} honour`);
    if (r.xpDelta > 0) tail.push(`+${formatBigNum(r.xpDelta)} XP`);
    if (r.itemDropped) tail.push(`Gift: ${r.itemDropped.qty}× ${r.itemDropped.name}`);
    if (r.leveledUp) tail.push('You reached a new level!');
    if (r.crownMultiplier < 1) {
      tail.push(`crowns cut to ${Math.round(r.crownMultiplier * 100)}% — spread your charity`);
    }
  } else {
    // FOILED — use the deed's themed mishap line, and note any injury.
    tail.push(r.failureBlurb ?? 'The moment passed unfulfilled.');
    if (r.injured) {
      const until = r.injuredUntil ? ` until ${new Date(r.injuredUntil).toLocaleTimeString()}` : '';
      tail.push(`You're carried to the Infirmary${until}.`);
    } else {
      tail.push('You come away shaken but unhurt — try again.');
    }
  }

  // An injury on a foiled deed is a genuine setback — show it in danger red.
  const kind: FlashState['kind'] =
    r.outcome === 'TRIUMPH' || r.outcome === 'PARTIAL'
      ? 'success'
      : r.injured
        ? 'danger'
        : 'warn';

  return { kind, message: `${head}\n${tail.join(' · ')}` };
}

/** Live countdown ("Hh Mm Ss") for fatigue, from an ISO reset time. Empty once
 *  the timer has elapsed. */
function fatigueCountdown(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** The inner HTML of a single deed card. Pure (no DOM/listeners) so it can be
 *  rebuilt in place after an attempt without a whole-page render. */
function deedCardInnerHtml(
  deed: GoodDeedListing,
  char: NonNullable<ReturnType<typeof store.get>['character']>,
  resolveDiscountPct: number,
): string {
  // Virtue requirement: show the floor; if the player is under it, flag it red
  // and show their current Virtue so the gap is obvious.
  const virtueShort =
    char.virtue < deed.virtueRequired
      ? `<span class="cg-v warn">${formatBigNum(deed.virtueRequired)}+ (you ${formatBigNum(char.virtue)})</span>`
      : `<span class="cg-v">${formatBigNum(deed.virtueRequired)}+</span>`;
  const artSlug = deed.key.replace(/\./g, '-');
  // Diminishing returns: show the EFFECTIVE crowns the next attempt would earn
  // (base × crownMultiplier), with a Fatigue row when reduced.
  const cm = deed.crownMultiplier ?? 1;
  const crownLo = Math.floor(deed.crowns[0] * cm);
  const crownHi = Math.floor(deed.crowns[1] * cm);
  const resetIso = deed.repeatResetAt;
  const cdText = fatigueCountdown(resetIso);
  const fatigueRow = cm < 1
    ? `<div class="cg-row"><span class="cg-k">Fatigue</span><span class="cg-v warn">−${Math.round((1 - cm) * 100)}% crowns${resetIso ? ` · clears in <span class="deed-fatigue-cd" data-reset="${escapeHtml(resetIso)}">${cdText}</span>` : ''}</span></div>`
    : '';
  // Great Undertakings endowment — coin pledged before the attempt.
  const stake = deed.stakeCrowns ?? 0;
  const walletCoversStake = stake === 0 || BigInt(char.crowns) >= BigInt(stake);
  const stakeRow = stake > 0
    ? `<div class="cg-row"><span class="cg-k">Endowment</span><span class="cg-v warn" title="Pledged before the attempt: returned with the reward on a Triumph, returned alone on a Partial, forfeited if the undertaking goes unfulfilled.">${formatCrowns(stake)} at risk</span></div>`
    : '';
  return `
    <img class="deed-art${deed.category === 'great_undertakings' ? ' deed-art--full' : ''}" src="/img/good-deeds/${artSlug}.webp" alt=""
      onerror="this.remove()" />
    <h3>${escapeHtml(deed.name)}</h3>
    <div class="blurb">${escapeHtml(deed.blurb)}</div>
    <div class="cg">
      <div class="cg-col">
        <div class="cg-head">Requires</div>
        <div class="cg-row"><span class="cg-k">Resolve</span>${renderResolveCost(deed.resolveCost, resolveDiscountPct)}</div>
        <div class="cg-row"><span class="cg-k">Virtue</span>${virtueShort}</div>
        ${stakeRow}
        ${deed.injuryChance > 0 ? `<div class="cg-row"><span class="cg-k">Injury risk</span><span class="cg-v warn">${Math.round(deed.injuryChance * 100)}% if foiled</span></div>` : ''}
      </div>
      <div class="cg-col">
        <div class="cg-head">Reward · on success</div>
        <div class="cg-row"><span class="cg-k">${stake > 0 ? 'Reward' : 'Crowns'}</span><span class="cg-v gain">${formatCrowns(crownLo)}–${formatCrowns(crownHi)}${stake > 0 ? ' + endowment back' : ''}</span></div>
        ${fatigueRow}
        <div class="cg-row"><span class="cg-k">Virtue</span><span class="cg-v gain">+${formatBigNum(deed.virtueOnSuccess)}</span></div>
        <div class="cg-row"><span class="cg-k">Honour</span><span class="cg-v gain">+${formatBigNum(deed.honourOnSuccess)}</span></div>
        ${deed.canDropItems ? `<div class="cg-row clickable" data-loot="${deed.key}" role="button" tabindex="0" title="See gift odds"><span class="cg-k">Gift</span><span class="cg-v gain">${Math.round(deed.dropChance * 100)}% yield ⓘ</span></div>` : ''}
      </div>
    </div>
    <button class="btn" data-attempt="${deed.key}" data-stake="${stake}" data-name="${escapeHtml(deed.name)}"
      ${char.status !== 'OKAY' || char.resolve < effResolve(deed.resolveCost, resolveDiscountPct) || !walletCoversStake ? 'disabled' : ''}>
      ${stake > 0 ? (walletCoversStake ? 'Pledge the endowment' : `Need ${formatCrowns(stake)} in hand`) : 'Perform'}
    </button>
  `;
}

/** Career ledger panel — mirrors the Misdeeds "Thy Ledger" grid. */
function deedLedgerHtml(ledger: GoodDeedLedgerDto | null): string {
  if (!ledger) return '';
  const done = ledger.triumphs + ledger.partials;
  const successRate = ledger.attempts > 0 ? Math.round((done / ledger.attempts) * 100) : 0;
  const block = (label: string, value: string | number, klass = ''): string =>
    `<div class="record-block ${klass}">
       <div class="label">${label}</div>
       <div class="value">${value}</div>
     </div>`;
  return `
    <section class="panel" id="ledger-panel">
      <h2 style="margin-top:0;">Thy Ledger of Good Deeds</h2>
      <div class="record-grid">
        ${block('Triumphs', formatBigNum(ledger.triumphs), 'win')}
        ${block('Partials', formatBigNum(ledger.partials))}
        ${block('Unfulfilled', formatBigNum(ledger.foiled))}
        ${block('Deeds done', formatBigNum(ledger.attempts))}
        ${block('Success rate', `${successRate}%`)}
        ${block('Virtue earned', formatBigNum(ledger.virtueEarned), 'win')}
        ${block('Honour earned', formatBigNum(ledger.honourEarned), 'win')}
        ${block('Crowns earned', formatCrowns(ledger.crownsEarned))}
      </div>
    </section>
  `;
}

/** Effective Resolve cost after the Pious Writs discount. Mirrors the server's
 *  Math.ceil(cost*(1-pct)) — so a 1-Resolve deed stays at 1. */
function effResolve(base: number, pct: number): number {
  if (pct <= 0) return base;
  return Math.max(1, Math.ceil(base * (1 - pct)));
}

/** Render the Resolve cost cell. With an active discount that actually changes
 *  the cost, show the base struck through and the reduced value beside it. */
function renderResolveCost(base: number, pct: number): string {
  const eff = effResolve(base, pct);
  if (eff < base) {
    return `<span class="cg-v gain" title="Pious Writs: −${Math.round(pct * 100)}% Resolve">`
      + `<s style="opacity:.6;font-weight:400">${base}</s> ${eff}</span>`;
  }
  return `<span class="cg-v">${base}</span>`;
}

